import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { EntityName } from '../../../auth/Security';
import type {
    DeepLinkUIStateInitializationEvent,
    DeepLinkUIStateOnBeforeInitializationHandler
} from '../../../deepLink/types';
import { useDeepLinkContext } from '../../../deepLink/ui/hooks/useDeepLinkContext';
import { useEntityDeepLinkUIStatePath } from '../../../deepLink/ui/hooks/useEntityDeepLinkUIStatePath';
import { extractDeepLinkUIStateInitializationEvent } from '../../../deepLink/ui/hooks/useInitializeDeepLinkUIState.utils';
import { useInitializeListViewDeepLinkUIState } from '../../../deepLink/ui/hooks/useInitializeListViewDeepLinkUIState';
import type { SidePanelCommandResult, SidePanelSubject } from '../../types';
import { useSidePanelCommands } from './useSidePanelCommands';

const getInitializationRecordId = (record: { id: string | undefined }): string | undefined => record.id;

export type SidePanelDeepLinkRestoreInput<T extends Record<string, unknown>> = {
    entity: EntityName;
    entityDeepLinkUIStatePath?: string;
    entityDataList: T[] | undefined;
    selectedEntityDataRecordId?: string;
    getRecordId: (record: T) => string | undefined;
    toSubject: (record: T) => SidePanelSubject | null;
    toPayload?: (record: T, event: DeepLinkUIStateInitializationEvent) => Record<string, unknown> | undefined;
    onBeforeInitialize?: DeepLinkUIStateOnBeforeInitializationHandler;
    /** Explicit panel for entity-node mappings (for example Deals), which the legacy initializer cannot infer. */
    panelId?: string;
    /** Resolves a subject for a validated, on-page row. No off-page fetching is performed by default. */
    resolveSubject?: (
        record: T,
        event: DeepLinkUIStateInitializationEvent,
        signal: AbortSignal
    ) => Promise<SidePanelSubject | null>;
};

/**
 * Restores a list URL through the existing initializer and Phase 1 panel commands.
 *
 * Waits for list data; missing/empty lists and unmatched IDs retain the legacy terminal fallback.
 * `resolveSubject` is only called for a row found by `getRecordId`; off-page restore requires a separate
 * owner integration. The restored ID is retained when no selection is supplied, preventing selection sync
 * from replacing the runtime entity node with `true`. An explicit selected ID takes precedence thereafter.
 * Initial linked-item nodes are not plucked here: panel content owns item-ID consumption and highlighting.
 * Mount one instance per entity/path inside the declaring owner and an existing panel surface.
 */
export function useSidePanelDeepLinkRestore<T extends Record<string, unknown>>({
    entity,
    entityDeepLinkUIStatePath,
    entityDataList,
    selectedEntityDataRecordId,
    getRecordId,
    toSubject,
    toPayload,
    onBeforeInitialize,
    panelId,
    resolveSubject
}: SidePanelDeepLinkRestoreInput<T>) {
    const deepLinkContext = useDeepLinkContext();
    const resolvedPath = useEntityDeepLinkUIStatePath(entity, entityDeepLinkUIStatePath);
    const { open } = useSidePanelCommands();
    const [restoredEntityId, setRestoredEntityId] = useState(() => {
        const initialNode = deepLinkContext.getDeepLinkInitialState<Record<string, unknown>>({ path: resolvedPath });
        const initialEvent = extractDeepLinkUIStateInitializationEvent(initialNode);
        return initialEvent.entityId;
    });
    const [restoreResult, setRestoreResult] = useState<SidePanelCommandResult>();
    const abortController = useRef<AbortController | undefined>(undefined);
    const initialization = useRef<Promise<void> | undefined>(undefined);
    const initializationRows = useMemo(
        () => entityDataList?.map((record) => ({ ...record, id: getRecordId(record) })),
        [entityDataList, getRecordId]
    );

    useEffect(() => {
        const controller = new AbortController();
        abortController.current = controller;
        return () => {
            controller.abort();
            initialization.current = undefined;
        };
    }, []);

    const initializePanel = useCallback(
        (event: DeepLinkUIStateInitializationEvent): Promise<void> => {
            if (initialization.current) {
                return initialization.current;
            }

            const initialize = async () => {
                const signal = abortController.current?.signal;
                if (!signal || signal.aborted) return;

                try {
                    await onBeforeInitialize?.(event);
                    if (signal.aborted) return;

                    const record = entityDataList?.find((row) => getRecordId(row) === event.entityId);
                    if (!record) return;
                    setRestoredEntityId(event.entityId);

                    const resolvedPanelId = panelId ?? event.sidePanelId;
                    if (!resolvedPanelId) return;

                    const subject = resolveSubject ? await resolveSubject(record, event, signal) : toSubject(record);
                    if (signal.aborted) return;

                    if (!subject) {
                        setRestoreResult({ status: 'rejected', reason: 'subjectMissing' });
                        return;
                    }

                    const result = await open(resolvedPanelId, { subject, payload: toPayload?.(record, event) });
                    if (signal.aborted) return;

                    switch (result.status) {
                        case 'rejected':
                        case 'failed':
                        case 'superseded':
                        case 'unhandled':
                            setRestoreResult(result);
                            return;
                        case 'ignored':
                        case 'ok':
                            setRestoreResult(result);
                            break;
                    }
                } catch (error) {
                    if (signal.aborted) return;
                    setRestoreResult({ status: 'failed', error });
                }
            };

            initialization.current = initialize();
            return initialization.current;
        },
        [entityDataList, getRecordId, onBeforeInitialize, open, panelId, resolveSubject, toPayload, toSubject]
    );

    useInitializeListViewDeepLinkUIState({
        entity,
        entityDeepLinkUIStatePath,
        entityDataList: initializationRows,
        selectedEntityDataRecordId: selectedEntityDataRecordId ?? restoredEntityId,
        getEntityDataRecordId: getInitializationRecordId,
        onBeforeInitialize: initializePanel
    });

    const restoreState = { restoredEntityId, restoreResult };
    return restoreState;
}
